import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isServicesEnabled } from '../../../shared/services/featureFlags';
import { servicesApi, type ServiceDto } from '../../../shared/services/api/servicesApi';
import { Header, Footer } from '../../layouts/index';
import * as styles from './servicesPage.css';

export default function ServicesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [services, setServices] = React.useState<ServiceDto[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const enabled = await isServicesEnabled();
        if (!enabled) {
          navigate('/404', { replace: true });
          return;
        }
        const list = await servicesApi.listPublic();
        if (mounted) setServices(list);
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Erreur de chargement');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  if (loading) {
    return (
      <>
        <Header />
        <main id="main-content" className={styles.container}>
          <div className={styles.loadingContainer}>
            <h1 className={styles.mainTitle}>Services</h1>
            <p className={styles.loadingText}>Chargement…</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <main id="main-content" className={styles.container}>
          <h1 className={styles.mainTitle}>Services</h1>
          <div className={styles.errorContainer} role="alert">
            {error}
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatPrice = (description: string) => {
    // Extraire et formater les prix du texte
    const priceMatch = description.match(/(\d+)€/);
    return priceMatch ? priceMatch[0] : null;
  };

  const getServiceIcon = (icon?: string) => {
    if (!icon) return '🔧';
    // Mapping simple des icônes MDI vers emoji
    const iconMap: Record<string, string> = {
      'mdi:web': '🌐',
      'mdi:code-tags': '💻', 
      'mdi:hammer-wrench': '🔧',
      'mdi:magnify': '🔍',
      'mdi:palette': '🎨',
      'mdi:animation': '✨',
      'mdi:cog': '⚙️',
      'mdi:responsive': '📱',
      'mdi:web-box': '📦',
      'mdi:brush': '🖌️',
      'mdi:shopping': '🛒',
      'mdi:wrench': '🔧',
      'mdi:code-braces': '💻'
    };
    return iconMap[icon] || '🔧';
  };

  return (
    <>
      <Header />
      <main id="main-content" className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.mainTitle}>Nos Services</h1>
          <p className={styles.subtitle}>
            Développeur Informatique Indépendant - Jean-François CHAUSSOY
          </p>
          <p className={styles.ctaText}>
            Une idée ? Un projet ? N'hésitez pas à demander un devis ! <strong>[GRATUIT]</strong>
          </p>
        </header>

        {!loading && !error && services.length === 0 && (
          <div className={styles.emptyContainer}>
            <p className={styles.emptyText}>Aucun service disponible pour le moment.</p>
          </div>
        )}

        {!loading && !error && services.length > 0 && (
          <>
            {/* Services métiers */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Nos Expertises</h2>
              <div className={styles.servicesGrid}>
                {services.filter(s => !s.slug.includes('tarif') && !s.slug.includes('standard') && !s.slug.includes('personnalise') && !s.slug.includes('ecommerce') && !s.slug.includes('maintenance')).map((service) => (
                  <article key={service.id} className={styles.serviceCard}>
                    <div className={styles.cardHeader}>
                      <span className={styles.serviceIcon}>
                        {getServiceIcon(service.icon)}
                      </span>
                      <h3 className={styles.cardTitle}>
                        {service.title_fr || service.title_en}
                      </h3>
                    </div>
                    <p className={styles.cardDescription}>
                      {service.description_fr || service.description_en}
                    </p>
                  </article>
                ))}
              </div>
            </section>

            {/* Offres tarifaires */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Nos Offres</h2>
              <div className={styles.offersGrid}>
                {services.filter(s => s.slug.includes('standard') || s.slug.includes('personnalise') || s.slug.includes('ecommerce') || s.slug.includes('maintenance') || s.slug.includes('tarif')).map((offer) => {
                  const price = formatPrice(offer.description_fr || offer.description_en || '');
                  const isHighlighted = offer.slug.includes('personnalise');
                  
                  return (
                    <article key={offer.id} className={isHighlighted ? styles.highlightedOfferCard : styles.offerCard}>
                      {isHighlighted && (
                        <div className={styles.popularBadge}>
                          POPULAIRE
                        </div>
                      )}
                      
                      <div className={styles.offerHeader}>
                        <span className={styles.offerIcon}>
                          {getServiceIcon(offer.icon)}
                        </span>
                        <div className={styles.offerInfo}>
                          <h3 className={styles.offerTitle}>
                            {offer.title_fr || offer.title_en}
                          </h3>
                          {price && (
                            <div className={isHighlighted ? styles.highlightedOfferPrice : styles.offerPrice}>
                              {price}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className={isHighlighted ? styles.highlightedOfferDescription : styles.offerDescription}>
                        {(offer.description_fr || offer.description_en || '').split('.').map((sentence, idx) => 
                          sentence.trim() && (
                            <div key={idx} className={styles.offerFeature}>
                              • {sentence.trim()}
                            </div>
                          )
                        )}
                      </div>
                      
                      <button className={isHighlighted ? styles.highlightedCtaButton : styles.ctaButton}>
                        Demander un devis
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>

            {/* Note légale */}
            <footer className={styles.footer}>
              <p className={styles.footerText}>
                <strong>Informations importantes :</strong><br/>
                L'ensemble des tarifs indiqués sont H.T. Prix à titre informatif, changement possible selon les demandes et choix du client.<br/>
                <strong>Droit d'auteur & propriété intellectuelle :</strong> Vous êtes, à la fin du projet, propriétaire de l'ensemble des sources composant celui-ci.<br/>
                <strong>Paiement possible en plusieurs fois.</strong>
              </p>
            </footer>
          </>
        )}
      </main>
      <Footer />
    </>
  );
}
